import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Field, PageHeader, Section } from "@/components/common/kit";

function BusinessProfile() {
  const [p, setP] = useState({ name: "", address: "", phone: "" });
  useEffect(() => { api.get("/settings/business-profile").then((r) => setP({ name: "", address: "", phone: "", ...r.data })); }, []);
  const save = async () => {
    try { await api.put("/settings/business-profile", p); toast.success("Business details saved — now shown on all prints"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-3" data-testid="business-profile-section">
      <Field label="Business name"><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="bp-name" /></Field>
      <Field label="Address"><Input value={p.address} onChange={(e) => setP({ ...p, address: e.target.value })} data-testid="bp-address" /></Field>
      <Field label="Phone"><Input value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} data-testid="bp-phone" /></Field>
      <p className="text-xs text-slate-500">Printed on bills, loading sheets, closing sheets, collection sheets, cashbook, statements, issue slips and PDF/Excel exports.</p>
      <Button onClick={save} disabled={p.name.trim().length < 2} data-testid="bp-save">Save business details</Button>
    </div>
  );
}

export function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password changed");
      setF({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <Field label="Current password"><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="current-password" /></Field>
      <Field label="New password" hint="Min 6 characters"><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password" /></Field>
      <Button onClick={save} disabled={!f.current_password || f.new_password.length < 6} data-testid="change-password-button">Change password</Button>
    </div>
  );
}

export default function SettingsPage() {
  const { settings, refreshSettings } = useAuth();
  const [date, setDate] = useState(settings?.business_date_override || settings?.system_date);
  const [cats, setCats] = useState(settings?.expense_categories || []);
  const [newCat, setNewCat] = useState("");
  useEffect(() => { if (settings) setCats(settings.expense_categories); }, [settings]);

  const setBiz = async (val) => {
    try {
      await api.put("/settings/business-date", { date: val });
      await refreshSettings();
      toast.success(val ? `Business date set to ${val}` : "Business date set to automatic");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveCats = async (list) => {
    try {
      await api.put("/settings/expense-categories", { categories: list });
      await refreshSettings();
      toast.success("Categories saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Business date control, expense categories and admin account" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Business Date" testid="business-date-section">
          <p className="mb-3 text-sm text-slate-600">
            Daily operations use the actual date (India time): <b>{fmtDate(settings?.system_date)}</b>. Override only for business-day corrections.
            Current business date: <b data-testid="current-business-date">{fmtDate(settings?.business_date)}</b>{settings?.business_date_override && " (override active)"}.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Override date"><Input type="date" value={date || ""} onChange={(e) => setDate(e.target.value)} data-testid="business-date-input" /></Field>
            <Button onClick={() => setBiz(date)} data-testid="business-date-save">Set override</Button>
            <Button variant="outline" onClick={() => setBiz(null)} disabled={!settings?.business_date_override} data-testid="business-date-clear">Use automatic</Button>
          </div>
        </Section>
        <Section title="Expense Categories" testid="categories-section">
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-sm">
                {c}<button onClick={() => saveCats(cats.filter((x) => x !== c))} data-testid={`remove-category-${c}`}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Input placeholder="New category" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-category-input" />
            <Button onClick={() => { saveCats([...cats, newCat.trim()]); setNewCat(""); }} disabled={!newCat.trim()} data-testid="add-category-button">Add</Button>
          </div>
        </Section>
        <Section title="Business Name & Details"><BusinessProfile /></Section>
        <Section title="Admin Password"><ChangePassword /></Section>
      </div>
    </div>
  );
}
